import React, { PureComponent, Fragment } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
  CartesianGrid,
} from "recharts";

import {
  StandardTooltip,
  selectFormat,
  Error,
  CustomizedYAxisTickSeconds,
} from "./../../Utils/Utils.js";

const fieldProps = [
  {
    dataKey: "time_spent",
    label: "Time Spent",
    yAxisId: "yAxisId",
    color: "#FC6D52",
    type: "bar",
    barSize: 40,
    format: "sec",
  },
  {
    dataKey: "runtime",
    label: "Runtime",
    yAxisId: "yAxisId",
    color: "#17a2b8",
    type: "bar",
    barSize: 40,
    format: "sec",
  },
];

class AvgAggregateTimeSpent extends PureComponent {
  constructor(props) {
    super(props);
    this._id = Math.random().toString(36).substr(2, 9);
  }

  componentDidMount() {
    this.props.childRef(this);
  }

  renderLegends = (props) =>
    props.map((entry, i) => (
      <span key={i} className="legend mt-2 mt-md-0">
        <span
          className="legend__icon"
          style={{ background: entry.color }}
        ></span>
        <span className="legend__text" style={{ color: entry.color }}>
          {entry.label}
        </span>
      </span>
    ));

  renderItems = (fieldProps) =>
    fieldProps.map((props) => {
      if (props.type === "bar")
        return (
          <Bar
            key={props.dataKey}
            dataKey={props.dataKey}
            yAxisId={props.yAxisId}
            barSize={props.barSize || 50}
            fill={`url(#color-bar-${props.dataKey}-${this._id})`}
          />
        );
      if (props.type === "area")
        return (
          <Area
            key={props.dataKey}
            type="monotone"
            dataKey={props.dataKey}
            yAxisId={props.yAxisId}
            fill={`url(#color-area-${props.dataKey}-${this._id})`}
            stroke={props.color}
            strokeWidth={2}
            dot={props.dot}
          />
        );
      if (props.type === "line")
        return (
          <Line
            key={props.dataKey}
            type="monotone"
            dataKey={props.dataKey}
            yAxisId={props.yAxisId}
            stroke={props.color}
          />
        );
      return null;
    });

  renderColorDefs = (fieldProps) =>
    fieldProps.map((props) => {
      if (props.type === "bar")
        return (
          <defs key={props.dataKey}>
            <linearGradient
              id={`color-bar-${props.dataKey}-${this._id}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="5%" stopColor={props.color} stopOpacity={1} />
              <stop offset="95%" stopColor={props.color} stopOpacity={0.55} />
            </linearGradient>
          </defs>
        );
      if (props.type === "area")
        return (
          <defs key={props.dataKey}>
            <linearGradient
              id={`color-area-${props.dataKey}-${this._id}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="5%" stopColor={props.color} stopOpacity={0.55} />
              <stop offset="95%" stopColor={props.color} stopOpacity={0.051} />
            </linearGradient>
          </defs>
        );
      return null;
    });

  getExportData = () => ({
    data: this.props.data.data.timeSpentViewsGraph,
    opts: {
      type: "chart",
      date: {
        no_date: true,
        title: "Snap",
        name: "label",
      },
      exclude: ["label"],
    },
  });

  render() {
    const { data } = this.props;

    if (!data.data) {
      return <Error minHeight={360} />;
    }

    const mapKeyToProps = {};
    fieldProps.forEach((props) => (mapKeyToProps[props.dataKey] = props));

    return (
      <Fragment>
        {this.props.nolegend ? null : this.renderLegends(fieldProps)}
        <ResponsiveContainer height={330}>
          <ComposedChart
            data={data.data.timeSpentViewsGraph}
            margin={{ top: 20, right: 0, left: 0, bottom: 10 }}
          >
            <Tooltip
              cursor={{ fill: "#eee", opacity: 0.5 }}
              content={
                <StandardTooltip
                  render={({ data }) => {
                    const payload = data(this.props);

                    if (payload !== null) {
                      const tooltip_data = payload[0].payload;

                      return (
                        <div className="standard-tooltip">
                          {tooltip_data.label && (
                            <header className="standard-tooltip__header">
                              <span className="standard-tooltip__value-header">
                                {tooltip_data.label}
                              </span>
                            </header>
                          )}
                          <main className="standard-tooltip__main">
                            {Object.keys(payload)
                              .sort(
                                (a, b) => payload[b].value - payload[a].value
                              )
                              .filter((key) => payload[key].value > 0)
                              .map((key, index) => {
                                const props =
                                  mapKeyToProps[payload[key].dataKey];
                                return (
                                  <div
                                    className="standard-tooltip__item"
                                    key={key}
                                  >
                                    <span
                                      className="standard-tooltip__icon"
                                      style={{ background: props.color }}
                                    ></span>
                                    <span
                                      className="standard-tooltip__key"
                                      style={{
                                        color: props.color,
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      {props.label}{" "}
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        props.format,
                                        payload[key].value
                                      )}
                                    </span>
                                  </div>
                                );
                              })}
                          </main>
                        </div>
                      );
                    }
                  }}
                />
              }
            />

            <CartesianGrid
              stroke="#FEE5DE"
              strokeDasharray="3 3"
              vertical={false}
            />

            {this.renderColorDefs(fieldProps)}
            {this.renderItems(fieldProps)}

            <XAxis dataKey="label" tickLine={false} axisLine={false} />
            <YAxis
              yAxisId={fieldProps[0].yAxisId}
              orientation="left"
              axisLine={false}
              tickLine={false}
              mirror={true}
              tick={<CustomizedYAxisTickSeconds />}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Fragment>
    );
  }
}

export default AvgAggregateTimeSpent;
